import { useMediaRemote, useMediaState } from '@vidstack/react';
import { useEffect, useRef, useState } from 'react';

const REV_STEPS = [0, 1, 2, 4, 8];
const FF_STEPS = [1, 2, 4, 8];

export const useRewindControl = () => {
    const remote = useMediaRemote();
    const isPaused = useMediaState('paused');
    const currentTime = useMediaState('currentTime');
    const [revSpeed, setRevSpeed] = useState(0);

    const timeRef = useRef(currentTime);
    useEffect(() => {
        timeRef.current = currentTime;
    }, [currentTime]);

    useEffect(() => {
        if (revSpeed === 0) return;
        const interval = setInterval(() => {
            const jumpAmount = 0.1 * revSpeed;
            const newTime = Math.max(0, timeRef.current - jumpAmount);
            remote.seek(newTime);
            if (newTime <= 0) setRevSpeed(0);
        }, 100);
        return () => clearInterval(interval);
    }, [revSpeed, remote]);

    const prevIsPaused = useRef(isPaused);
    useEffect(() => {
        if (
            prevIsPaused.current === true &&
            isPaused === false &&
            revSpeed > 0
        ) {
            setRevSpeed(0);
        }
        prevIsPaused.current = isPaused;
    }, [isPaused, revSpeed]);

    const handleRewind = (e: React.MouseEvent) => {
        e.stopPropagation();
        const nextSpeed =
            REV_STEPS[
                (Math.max(0, REV_STEPS.indexOf(revSpeed)) + 1) %
                    REV_STEPS.length
            ];
        setRevSpeed(nextSpeed);
        if (nextSpeed > 0) remote.pause();
        else {
            remote.changePlaybackRate(1);
            remote.play();
        }
    };

    return { revSpeed, handleRewind };
};

export const useFastForwardControl = () => {
    const remote = useMediaRemote();
    const isPaused = useMediaState('paused');
    const currentRate = useMediaState('playbackRate');

    const handleFF = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isPaused) {
            remote.changePlaybackRate(1);
            remote.play();
        } else {
            const nextRate =
                FF_STEPS[
                    (Math.max(0, FF_STEPS.indexOf(currentRate)) + 1) %
                        FF_STEPS.length
                ];
            remote.changePlaybackRate(nextRate);
        }
    };

    const isActive = !isPaused && currentRate > 1;
    return { currentRate, isActive, handleFF };
};
